@file:UseSerializers(BigDecimalSerializer::class)
@file:OptIn(ExperimentalTime::class)

package net.sergeych.ugdx.shared.trader

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import net.sergeych.sprintf.format
import net.sergeych.sprintf.sprintf
import net.sergeych.ugdx.shared.BigDecimalSerializer
import net.sergeych.ugdx.shared.api.TradeOrderLogArgs
import net.sergeych.ugdx.shared.api.cmdTradeOrderLog
import net.sergeych.ugdx.shared.api.invoke
import kotlin.time.Duration.Companion.days
import kotlin.time.ExperimentalTime

/**
 * The exchange trading order. All fields are available only to order owner, otherwise some fields
 * are set to null.
 */
@Serializable
data class TradeOrder(
    val id: Long,
    val isBuy: Boolean,
    val isAllOrNothing: Boolean,
    val initialAmount: BigDecimal?,
    val amount: BigDecimal,
    val limit: BigDecimal?,
    val stop: String?,
    val sellAccountId: Long?,
    val buyAccountId: Long?,
    val status: Status = Status.NEW,
    val createdAt: Instant = Clock.System.now(),
    val expiresAt: Instant? = Clock.System.now().plus(365.days)
) {
    /**
     * Order lifespan statuses. There are more information on trading order life history available
     * with special operation class not yet included into api.
     */
    enum class Status {
        /**
         * Order is not yet added to the orderbook
         */
        NEW,

        /**
         * Order was processed at least once and now added to orderbook for matching
         */
        ACTIVE,
        /**
         * Order is executed, either completely, or partially, or event not actually executed, but
         * came to its natural end of life
         */
        EXECUTED,
        /**
         * Expired order: was closed before normal end because of expiration
         */
        EXPIRED,
        /**
         * Order was explicitly removed by the request.
         */
        REMOVED,

        /**
         * Order was found invalid
         */
        REJECTED;

        val isSuitableForPorcessing: Boolean by lazy { this == NEW || this == ACTIVE }
    }

    override fun toString(): String {
        val op = if (isBuy) "Buy " else "Sell"
        val anMark = if (isAllOrNothing) ":A/N" else ""
        val price = limit?.let { "@${it.toPlainString()}$anMark" } ?: "@market"
        val byPart = expiresAt?.let { " by $it" } ?: ""
        val ifClause = stop?.let { cond ->
            if (status == Status.ACTIVE)
                " met: $cond"
            else
                " when: $cond"
        }
            ?: ""
        return "$id:$op ${"%10f".format(amount.doubleValue(false))} $price$ifClause$byPart ${status.name}"

    }

    @Serializable
    data class Log(
        val id: Long,
        val type: Type,
        val amount: BigDecimal?,
        val rate: BigDecimal?,
        val text: String?,
        val createdAt: Instant
    ) {
        enum class Type {
            STATE_CHANGED,
            STOP_ACTIVATED,
            DEAL,
            EXECUTED,
            REJECTED
        }

        override fun toString(): String {
            val dt = "%tF %1!tT %14s".sprintf(createdAt, type)
            return if( amount != null )
                "$dt ${amount.toPlainString()} @${rate?.toPlainString() ?: "---"}"
            else
                "$dt $text"
        }
    }

    @Serializable
    data class LogResult(
        val records: List<Log>,
        val hasMore: Boolean
    )

    /**
     * Get portion of the order operation log. It is always sorted least recent first (chronologically), so to get
     * the rest of the log use the greatest known `id` value of [TradeOrder.Log].
     *
     * Please note that only [LogResult.hasMore] value should be use to check that there are more records.
     *
     * @param beforeId of not null, limit records to these older than a given id
     * @param limit desired number ot records. Actual number could be arbitrary not depending on availbility, so check
     *              [LogResult.hasMore].
     */
    suspend fun log(afterId: Long=0L,limit: Int=100): LogResult =
        cmdTradeOrderLog.invoke(TradeOrderLogArgs(id, afterId, limit))

    /**
     * Same as [Trader.refreshOrder]
     */
    suspend fun refresh(): TradeOrder =
        Trader.refreshOrder(this)
}